/**
 * `<FixAndResolveHost/>` — mounts *Fix and re-solve* once, in the
 * right-panel overlay (#1960).
 *
 * The app shell renders it next to the route outlet. It shows nothing until
 * a door — the ladder's out-sick footer, the Review cover inbox or (#2029)
 * the roster toolbar's standing button — arms
 * `useFixAndResolveStore`; then the panel opens as a 480 px non-modal side
 * panel (the ladder's width, `CycleSurfaceModal` `lg`), which takes the one
 * drawer slot, so the ladder closes as it opens. After an apply the panel
 * closes and a short line confirms it: `Applied N changes`.
 *
 * #1988 — it also tells the review screen where the pins land (the team and
 * the window the flow opened on), which is what lets a pinned row offer
 * **Unpin** there.
 */
import { useEffect, useState } from "react";

import { reviewDate } from "../../lib/changeReview";
import { type FixAndResolveRequest, useFixAndResolveStore } from "../../store/fixAndResolveRequest";
import { CycleSurfaceModal } from "../cycle/CycleSurfaceModal";
import { ReviewUnpinContext } from "../review/ReviewPinControl";
import { Toast } from "../ui/Toast";
import { FixAndResolvePanel } from "./FixAndResolvePanel";

const TOAST_MS = 4000;

/** The line under the title: the door's day, or (#2029) the dates it names. */
function requestDescription(request: FixAndResolveRequest): string {
  const d = request.dates;
  if (!d) return reviewDate(request.date);
  return d.from === d.to ? reviewDate(d.from) : `${reviewDate(d.from)} – ${reviewDate(d.to)}`;
}

export function FixAndResolveHost() {
  const request = useFixAndResolveStore((s) => s.request);
  const close = useFixAndResolveStore((s) => s.closeFixAndResolve);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => window.clearTimeout(t);
  }, [toast]);

  return (
    <>
      <CycleSurfaceModal
        open={request !== null}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        title="Fix and re-solve"
        {...(request ? { description: requestDescription(request) } : {})}
        testid="fix-resolve-surface"
        panelWidth="lg"
      >
        {request ? (
          <ReviewUnpinContext.Provider value={{ teamId: request.teamId, cycleId: request.cycleId }}>
            <FixAndResolvePanel
              // A new door opening starts the flow afresh.
              key={`${request.cycleId}|${request.date}|${request.dates?.from ?? ""}|${request.dates?.to ?? ""}|${request.absent.map((a) => a.membership_id).join(",")}`}
              request={request}
              onApplied={(n) => {
                close();
                setToast(`Applied ${n} change${n === 1 ? "" : "s"}`);
              }}
            />
          </ReviewUnpinContext.Provider>
        ) : null}
      </CycleSurfaceModal>
      {/* #2172 — slides up from the corner, and back down. */}
      <Toast message={toast} testid="fix-resolve-toast" />
    </>
  );
}

export default FixAndResolveHost;
